import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetProfile, customFetch } from "@workspace/api-client-react";
import {
  getPrivacyChangeSummary,
  getTermsChangeSummary,
} from "@workspace/db/policy-versions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { PenLine, Loader2 } from "lucide-react";

// Task #58 — first-use acknowledgement page. Single checkbox per spec.
// AuthGuard sends signed-in users here when their stored
// privacyAcknowledgedVersion / termsAcknowledgedVersion don't match the
// constants the server echoes in the profile response.
export default function Acknowledge() {
  const [, setLocation] = useLocation();
  // Capture the route the user was originally trying to reach (the
  // AuthGuard stashes this in sessionStorage before redirecting here)
  // so a successful tick returns them there rather than always landing
  // on /app/diary.
  const returnTo =
    (typeof window !== "undefined" &&
      window.sessionStorage.getItem("ack:returnTo")) ||
    "/app/diary";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: profile, isLoading } = useGetProfile();

  const [ticked, setTicked] = useState(false);

  const currentPrivacyVersion = profile?.currentPrivacyVersion;
  const currentTermsVersion = profile?.currentTermsVersion;
  const alreadyAcknowledged =
    !!profile &&
    profile.privacyAcknowledgedVersion === currentPrivacyVersion &&
    profile.termsAcknowledgedVersion === currentTermsVersion;

  // Task #101 — distinguish a returning teacher being re-prompted because
  // we bumped a policy version from a brand-new teacher seeing the
  // acknowledgement screen for the first time. Only the former gets the
  // "What's changed since you last agreed" notice; first-timers have
  // nothing to compare against.
  const previouslyAcknowledged =
    !!profile &&
    (profile.privacyAcknowledgedVersion != null ||
      profile.termsAcknowledgedVersion != null);
  const privacyChanged =
    !!profile &&
    profile.privacyAcknowledgedVersion != null &&
    profile.privacyAcknowledgedVersion !== currentPrivacyVersion;
  const termsChanged =
    !!profile &&
    profile.termsAcknowledgedVersion != null &&
    profile.termsAcknowledgedVersion !== currentTermsVersion;
  const privacyChangeSummary = currentPrivacyVersion
    ? getPrivacyChangeSummary(currentPrivacyVersion)
    : null;
  const termsChangeSummary = currentTermsVersion
    ? getTermsChangeSummary(currentTermsVersion)
    : null;

  useEffect(() => {
    if (alreadyAcknowledged) setLocation("/app/diary");
  }, [alreadyAcknowledged, setLocation]);

  const acknowledge = useMutation({
    mutationFn: () =>
      customFetch<Record<string, unknown>>("/api/profile", {
        method: "PUT",
        body: JSON.stringify({
          privacyAcknowledgedAt: new Date().toISOString(),
          privacyAcknowledgedVersion: currentPrivacyVersion,
          termsAcknowledgedVersion: currentTermsVersion,
        }),
      }),
    onSuccess: async (updated) => {
      // Seed cache + wait for refetch so AuthGuard doesn't bounce us
      // back to /acknowledge before the new versions land.
      queryClient.setQueriesData({ queryKey: ["/api/profile"] }, () => updated);
      await queryClient.refetchQueries({ queryKey: ["/api/profile"] });
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem("ack:returnTo");
      }
      setLocation(returnTo);
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't save your acknowledgement",
        description: getApiErrorMessage(
          err,
          "Please refresh the page to load the current policy version, then try again.",
        ),
        variant: "destructive",
      });
    },
  });

  if (isLoading || !profile) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const canSubmit = ticked && !acknowledge.isPending;

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="px-6 py-4 flex items-center border-b border-border bg-white">
        <Link href="/" className="flex items-center gap-2">
          <PenLine className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold tracking-tight text-card-foreground">
            ClassTrack
          </span>
        </Link>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">
            {previouslyAcknowledged
              ? "We've updated our policies"
              : "Before you start"}
          </h1>

          {previouslyAcknowledged && (privacyChanged || termsChanged) ? (
            <div
              className="bg-sky-50 border border-sky-100 rounded-xl p-5 mb-6 text-sm text-sky-900 leading-relaxed"
              data-testid="panel-acknowledge-whats-new"
            >
              <p className="font-medium mb-2">
                What's changed since you last agreed
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                {privacyChanged && privacyChangeSummary ? (
                  <li data-testid="text-acknowledge-privacy-changes">
                    <span className="font-medium">Privacy Policy:</span>{" "}
                    {privacyChangeSummary}
                  </li>
                ) : null}
                {termsChanged && termsChangeSummary ? (
                  <li data-testid="text-acknowledge-terms-changes">
                    <span className="font-medium">Terms of Service:</span>{" "}
                    {termsChangeSummary}
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}

          <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 mb-6 text-sm text-gray-700 leading-relaxed">
            <p>
              ClassTrack helps you keep your own teacher records, lesson
              plans and diary. A few things to know before you start:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                ClassTrack stores the information you choose to enter,
                including student names, observations, behavioural notes
                and marks.
              </li>
              <li>
                You can reduce unnecessary detail by using the least
                identifying student label that works — initials,
                preferred names or student codes are all supported.
              </li>
              <li>
                The optional AI features apply best-effort redaction
                before sending text to OpenAI. Redaction is not perfect
                and does not make the data anonymous or de-identified,
                so please avoid entering unnecessary sensitive details.
              </li>
              <li>
                ClassTrack supports your record-keeping and exports but
                does not replace official school systems such as Sentral,
                Compass or equivalent.
              </li>
              <li>
                ClassTrack uses trusted cloud services to run the app,
                some of which may store or process information outside
                Australia. As with any teacher tool, please make sure
                your use of ClassTrack fits your school's policies
                before entering student-related information.
              </li>
            </ul>
          </div>

          <p className="text-sm text-gray-700 mb-6">
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
              data-testid="link-acknowledge-privacy"
            >
              Read the Privacy Policy
            </a>
            {" · "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
              data-testid="link-acknowledge-terms"
            >
              Read the Terms of Service
            </a>
          </p>

          <label className="flex items-start gap-3 cursor-pointer group">
            <Checkbox
              checked={ticked}
              onCheckedChange={(v) => setTicked(v === true)}
              className="mt-0.5"
              data-testid="checkbox-acknowledge"
            />
            <span className="text-sm text-gray-700 leading-relaxed group-hover:text-gray-900">
              I have read the Privacy Policy and Terms and understand
              that ClassTrack uses cloud services, some of which may
              process information outside Australia. I am responsible
              for using ClassTrack in line with my school's policies.
            </span>
          </label>

          <div className="mt-8 flex items-center justify-end">
            <Button
              onClick={() => acknowledge.mutate()}
              disabled={!canSubmit}
              className="gap-2"
              data-testid="button-acknowledge-continue"
            >
              {acknowledge.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Continue
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
